import { extractMarkdownReferences } from '@/components/editor/extractMarkdownReferences';
import { TicketStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import { uploadAttachment } from '@/features/attachments/uploadAttachment';
import { usePostTicketEventsMutation } from '@/features/tickets/tickets.hooks';
import { markdownReferencesToMeta } from '@/lib/markdownReferencesToMeta';
import { processPlaceholderTickets } from '@/lib/processPlaceholderTickets';
import {
  createTicketEvent,
  EventMeta,
  Position,
  SessionUser,
  TicketEvent,
  User,
} from '@certhon/models';
import { useCallback } from 'react';

export function useTicketMethods(
  userMap: Map<string | number, User>,
  ticketStore: TicketStore,
  user: SessionUser,
  ticketId: string,
  events: TicketEvent[],
) {
  const postTicketEvents = usePostTicketEventsMutation();

  const addMessage = useCallback(
    async (message: string) => {
      // Get ticket data for placeholder processing
      const ticket = ticketStore.getTicket(ticketId);

      // Process placeholder tickets in message
      const { markdown: processedMessage, ticketEvents } =
        processPlaceholderTickets(message, {
          userId: user.recnum,
          projectId: ticket?.project_id,
          werkId: ticket?.werk_id,
          clientId: ticket?.client_id,
          originTicketId: ticketId,
        });

      // Extract references and build meta
      let meta: EventMeta | undefined;
      const references = extractMarkdownReferences(processedMessage);
      if (references.length) {
        meta = markdownReferencesToMeta(references, userMap, ticketStore);
      }

      // Post placeholder tickets and message atomically
      const eventsToPost: TicketEvent[] = [...ticketEvents];
      eventsToPost.push(
        createTicketEvent('ADD_MESSAGE', {
          user_id: user.recnum,
          ticket_id: ticketId,
          payload: { message: processedMessage },
          meta,
        }),
      );

      await postTicketEvents.mutateAsync(eventsToPost);
    },
    [postTicketEvents, ticketId, ticketStore, user.recnum, userMap],
  );

  const addAttachments = useCallback(
    async (files: File[]) => {
      const events = [];
      for (const file of files) {
        const attachment = await uploadAttachment(file);
        events.push(
          createTicketEvent('ADD_ATTACHMENT', {
            user_id: user.recnum,
            ticket_id: ticketId,
            payload: {
              checksum: attachment.checksum,
              size: attachment.size,
              contentType: attachment.contentType,
              filename: attachment.filename ?? file.name,
            },
          }),
        );
      }
      await postTicketEvents.mutateAsync(events);
    },
    [postTicketEvents, ticketId, user.recnum],
  );

  const addLocation = useCallback(
    (location: { position: Position; label: string }) =>
      postTicketEvents.mutateAsync([
        createTicketEvent('ADD_LOCATION', {
          user_id: user.recnum,
          ticket_id: ticketId,
          payload: {
            position: location.position,
            label: location.label,
          },
        }),
      ]),
    [postTicketEvents, ticketId, user.recnum],
  );

  const updateMessage = useCallback(
    async (eventId: string, message: string) => {
      // Get ticket data for placeholder processing
      const ticket = ticketStore.getTicket(ticketId);

      // Process placeholder tickets in message
      const { markdown: processedMessage, ticketEvents } =
        processPlaceholderTickets(message, {
          userId: user.recnum,
          projectId: ticket?.project_id,
          werkId: ticket?.werk_id,
          clientId: ticket?.client_id,
          originTicketId: ticketId,
        });

      // Extract references and build meta
      let meta: EventMeta | undefined;
      const references = extractMarkdownReferences(processedMessage);
      if (references.length) {
        meta = markdownReferencesToMeta(references, userMap, ticketStore);
      }

      // Post placeholder tickets and update atomically
      const eventsToPost: TicketEvent[] = [...ticketEvents];
      eventsToPost.push(
        createTicketEvent('UPDATE_MESSAGE', {
          user_id: user.recnum,
          ticket_id: ticketId,
          payload: { eventId, message: processedMessage },
          meta,
        }),
      );

      return postTicketEvents.mutateAsync(eventsToPost);
    },
    [postTicketEvents, ticketId, ticketStore, user.recnum, userMap],
  );

  const deleteEvent = useCallback(
    (eventId: string) => {
      const event = events.find((e) => e.id === eventId);
      if (!event) return Promise.reject(new Error('Event not found'));

      let deleteEventType:
        | 'DELETE_MESSAGE'
        | 'DELETE_ATTACHMENT'
        | 'DELETE_LOCATION';
      switch (event.type) {
        case 'ADD_MESSAGE':
        case 'UPDATE_MESSAGE':
          deleteEventType = 'DELETE_MESSAGE';
          break;
        case 'ADD_ATTACHMENT':
          deleteEventType = 'DELETE_ATTACHMENT';
          break;
        case 'ADD_LOCATION':
        case 'UPDATE_LOCATION':
          deleteEventType = 'DELETE_LOCATION';
          break;
        default:
          return Promise.reject(new Error('Cannot delete this event type'));
      }

      return postTicketEvents.mutateAsync([
        createTicketEvent(deleteEventType, {
          user_id: user.recnum,
          ticket_id: ticketId,
          payload: { eventId },
        }),
      ]);
    },
    [events, postTicketEvents, ticketId, user.recnum],
  );
  return {
    deleteEvent,
    postTicketEvents,
    updateMessage,
    addMessage,
    addAttachments,
    addLocation,
  };
}
